import React, { useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { BuyerOrderService } from '../../../services/buyerOrder.service';
import { IBondRegisterReport, IBondRegisterRow } from '../../../interfaces/commercial.interface';
import { IBuyerOrder } from '../../../interfaces/garments/buyerOrder.interface';
import { triggerDownload } from '../../statutoryRegisters/shared';
import { enumLabel } from '../../../utils/garmentsLabels';

/** "CODE — Name" when both are known, else the raw item id (see the DTO's Javadoc for when that happens). */
const formatItem = (row: IBondRegisterRow): string =>
    row.itemCode && row.itemName ? `${row.itemCode} — ${row.itemName}` : row.itemId;

/**
 * Bond register report (T8, brief item 13.3) -- imports vs consumption vs BOM-allowed-with-wastage
 * per material item, for one buyer order. No default order: the operator must pick one via the
 * Autocomplete below (reusing `BuyerOrderService.list`, per task brief), matching the backend's
 * mandatory `buyerOrderId` query param. Mirrors `LcPositionReport`'s load/download shape.
 */
const BondRegisterReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState<IBuyerOrder | null>(null);
    const [report, setReport] = useState<IBondRegisterReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setOrdersLoading(true);
                const res = await BuyerOrderService.list(institutionId, 0, 200);
                setOrders(res.content);
            } catch (e) {
                toast.showApiError(e, 'Failed to load buyer orders');
            } finally {
                setOrdersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const load = async (order: IBuyerOrder) => {
        setLoading(true);
        try {
            const data = await CommercialReportService.getBondRegister(institutionId, order.id);
            setReport(data);
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load bond register report');
        } finally {
            setLoading(false);
        }
    };

    const handleOrderChange = (order: IBuyerOrder | null) => {
        setSelectedOrder(order);
        setReport(null);
        if (order) load(order);
    };

    const handleDownload = async () => {
        if (!selectedOrder) return;
        setDownloading(true);
        try {
            const blob = await CommercialReportService.downloadBondRegisterXlsx(institutionId, selectedOrder.id);
            triggerDownload(blob, `bond-register-${selectedOrder.orderNumber}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box data-help-page="commercialReports.bond.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Bond Register</Typography>
            <Box data-help-section="commercialReports.bond.filters" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Autocomplete data-help="commercialReports.bond.order"
                    sx={{ flex: '1 1 320px', maxWidth: 440 }}
                    size="small"
                    options={orders}
                    loading={ordersLoading}
                    value={selectedOrder}
                    getOptionLabel={(o) => o.orderNumber}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) => handleOrderChange(value)}
                    renderInput={(params) => <TextField {...params} label="Buyer Order" placeholder="Select a buyer order" />}
                />
                <Button data-help="commercialReports.bond.download"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || downloading}
                >
                    Export XLSX
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!selectedOrder && !loading && (
                <Typography color="text.secondary">Select a buyer order to view its bond register.</Typography>
            )}

            {!loading && selectedOrder && report && (
                <>
                    {report.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="bond-register-truncated">
                            Showing {report.rows.length} of {report.totalMatchingRows} matching rows.
                        </Alert>
                    )}

                    <TableContainer data-help-section="commercialReports.bond.table" component={Paper} data-testid="bond-register-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.bond.col.item">Item</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.type">Material Type</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.uom">UoM</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.imported" align="right">Imported Qty</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.consumed" align="right">Consumed Qty</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.allowed" align="right">Allowed Qty</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.variance" align="right">Variance</TableCell>
                                    <TableCell data-help="commercialReports.bond.col.wastage">Wastage</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary">No bond register rows for this order.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.rows.map(row => (
                                    <TableRow
                                        key={row.itemId}
                                        hover
                                        data-testid={`bond-register-row-${row.itemId}`}
                                        sx={row.overWastage ? { backgroundColor: 'error.50' } : undefined}
                                    >
                                        <TableCell>{formatItem(row)}</TableCell>
                                        <TableCell>{enumLabel(row.materialType)}</TableCell>
                                        <TableCell>{row.uom ?? '—'}</TableCell>
                                        <TableCell align="right">{row.importedQty}</TableCell>
                                        <TableCell align="right">{row.consumedQty}</TableCell>
                                        <TableCell align="right">{row.allowedQty}</TableCell>
                                        <TableCell align="right">{row.variance}</TableCell>
                                        <TableCell>
                                            {row.overWastage
                                                ? <Chip size="small" color="error" label="Over Wastage" data-testid={`bond-register-overwastage-${row.itemId}`} />
                                                : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default BondRegisterReport;
